import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'gold' | 'navy' | 'outline-light' | 'outline-dark'

interface ButtonLinkProps {
    href: string
    variant?: ButtonVariant
    external?: boolean
    className?: string
    children: ReactNode
}

interface TextLinkProps {
    href: string
    external?: boolean
    tone?: 'light' | 'dark'
    className?: string
    children: ReactNode
}

const buttonBase = 'inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 text-[0.95rem] font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2'

const buttonVariants: Record<ButtonVariant, string> = {
    'gold': 'bg-gold text-navy hover:brightness-110 focus-visible:outline-gold',
    'navy': 'bg-navy text-white hover:bg-navy-soft focus-visible:outline-navy',
    'outline-light': 'border border-white/40 text-white hover:bg-white/10 focus-visible:outline-white',
    'outline-dark': 'border border-navy/25 bg-white text-navy hover:bg-sand focus-visible:outline-navy'
}

const NewTabNote: FC = (): ReactNode => (
    <span className='sr-only'> (opens in a new tab)</span>
)

export const ButtonLink: FC<ButtonLinkProps> = ({ href, variant = 'navy', external, className, children }): ReactNode => {
    const classes = cn(buttonBase, buttonVariants[variant], className)

    if (external) {
        return (
            <a href={href} target='_blank' rel='noopener noreferrer' className={classes}>
                {children}
                <ExternalLink aria-hidden='true' className='size-4' />
                <NewTabNote />
            </a>
        )
    }

    return (
        <Link href={href} className={classes}>
            {children}
        </Link>
    )
}

export const TextLink: FC<TextLinkProps> = ({ href, external, tone = 'light', className, children }): ReactNode => {
    const classes = cn(
        'group inline-flex items-center gap-1.5 rounded-sm font-semibold underline-offset-4 decoration-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2',
        tone === 'dark'
            ? 'text-white decoration-gold focus-visible:outline-gold'
            : 'text-navy decoration-gold focus-visible:outline-navy',
        className
    )

    const iconClass = cn('size-4 shrink-0', tone === 'dark' ? 'text-gold' : 'text-gold-deep')

    if (external) {
        return (
            <a href={href} target='_blank' rel='noopener noreferrer' className={classes}>
                {children}
                <ExternalLink aria-hidden='true' className={iconClass} />
                <NewTabNote />
            </a>
        )
    }

    return (
        <Link href={href} className={classes}>
            {children}
            <ArrowRight aria-hidden='true' className={cn(iconClass, 'transition-transform group-hover:translate-x-0.5')} />
        </Link>
    )
}
